import React, { useEffect, useState } from 'react';
import '../styles/Login.css';
import appLogo from '../assets/app-icon.jpg';
import { useNavigate } from 'react-router-dom';
import emailjs from '@emailjs/browser';
import { useAuth } from '../context/AuthContext';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [forgotPassword, setForgotPassword] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const navigate = useNavigate();
  const { login, authFetch } = useAuth();
  const API_URL = process.env.REACT_APP_API_URL;

  // Initialize EmailJS
  useEffect(() => {
    emailjs.init('SV0XPhI3tDyRALbSk');
  }, []);

  // Check OAuth redirect on mount
  useEffect(() => {
    const fragment = window.location.hash;
    if (fragment.includes('session_id=')) {
      const sessionId = fragment.split('session_id=')[1];
      if (sessionId) handleOAuthLogin(sessionId);
    }
  }, []);

  // Handle OAuth login
  const handleOAuthLogin = async (sessionId) => {
    try {
      const response = await authFetch(`${API_URL}/auth/oauth/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId })
      });

      const data = await response.json();
      if (response.ok) {
        login(data.user, data.accessToken);
        navigate('/dashboard');
      } else {
        setErrorMessage(data.error || 'OAuth authentication failed');
      }
    } catch (error) {
      console.error('OAuth error:', error);
      setErrorMessage('OAuth authentication failed');
    }
  };

  // Google OAuth
  const handleGoogleLogin = () => {
    const redirectUrl = `${window.location.origin}/profile`;
    const authUrl = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(
      redirectUrl
    )}`;
    window.location.href = authUrl;
  };

  // Traditional login
  const validLogin = async (e) => {
    e.preventDefault();
    setErrorMessage('');

    if (!email || !password) {
      setErrorMessage('Email and password are required');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/valid-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();
      if (data.error) setErrorMessage(data.error);
      else if (data.message === 'Login successful') {
        login(data.user, data.accessToken);

        // Redirect based on role
        switch (data.user.role) {
          case 'guide':
            navigate('/guide-dashboard');
            break;
          case 'vehicle':
            navigate('/vehicle-dashboard');
            break;
          case 'admin':
            navigate('/admin-dashboard');
            break;
          default:
            navigate('/dashboard'); // normal user
        }
      }
      else setErrorMessage(data.message || 'Unknown error');
    } catch (err) {
      console.error(err);
      setErrorMessage('An error occurred. Please try again later.');
    }
  };

  // Send OTP for forgot password
  const sendOtp = async () => {
    setErrorMessage('');
    if (!email) {
      setErrorMessage('Please enter your email');
      return;
    }

    const generatedOtp = Math.floor(100000 + Math.random() * 900000);

    try {
      await emailjs.send('service_npgj14s', 'template_5671qrw', {
        email,
        subject: 'SoulfulYatra Password Reset OTP',
        message: `Your OTP is ${generatedOtp}. It expires in 10 minutes.`
      });

      const res = await fetch(`${API_URL}/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, otp: generatedOtp })
      });

      const data = await res.json();
      if (data.error) setErrorMessage(data.error);
      else setOtpSent(true);
    } catch (err) {
      console.error('EmailJS error:', err);
      setErrorMessage('Failed to send OTP. Please try again later.');
    }
  };

  // Reset password
  const resetPassword = async () => {
    setErrorMessage('');
    if (!otp || !newPassword || !confirmPassword) {
      setErrorMessage('All fields are required');
      return;
    }
    if (newPassword !== confirmPassword) {
      setErrorMessage('Passwords do not match');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, otp, newPassword })
      });

      const data = await res.json();
      if (data.error) setErrorMessage(data.error);
      else {
        setSuccessMessage('Password reset successful! Please log in.');
        setForgotPassword(false);
        setOtpSent(false);
        setEmail('');
        setPassword('');
        setOtp('');
        setNewPassword('');
        setConfirmPassword('');
      }
    } catch (err) {
      console.error(err);
      setErrorMessage('Error resetting password. Try again.');
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <img src={appLogo} alt="SoulfulYatra Logo" className="login-logo" />
        <h1 className="login-title">Welcome Back</h1>
        <p className="login-subtitle">
          Log in to continue your adventures with SoulfulYatra
        </p>

        {errorMessage && <p className="error-message">{errorMessage}</p>}
        {successMessage && <p className="success-message">{successMessage}</p>}

        {/*!forgotPassword && (
          <div className="oauth-section">
            <button
              onClick={handleGoogleLogin}
              className="google-login-btn"
              type="button"
            >
              Continue with Google
            </button>
            <div className="divider">
              <span>or</span>
            </div>
          </div>
        )*/}

        {!forgotPassword && (
          <form className="login-form" onSubmit={validLogin}>
            <label>Email</label>
            <input
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <label>Password</label>
            <input
              type="password"
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <button type="submit" className="login-btn">
              Log In
            </button>
          </form>
        )}

        {forgotPassword && !otpSent && (
          <div className="login-form">
            <label>Email</label>
            <input
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <button onClick={sendOtp} className="login-btn">
              Send OTP
            </button>
          </div>
        )}

        {forgotPassword && otpSent && (
          <div className="login-form">
            <label>OTP</label>
            <input
              type="text"
              placeholder="Enter OTP"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
            />
            <label>New Password</label>
            <input
              type="password"
              placeholder="New password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
            <label>Confirm Password</label>
            <input
              type="password"
              placeholder="Confirm new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
            <button onClick={resetPassword} className="login-btn">
              Reset Password
            </button>
          </div>
        )}

        {!forgotPassword && (
          <p className="login-footer">
            <a href="#!" onClick={() => setForgotPassword(true)}>
              Forgot Password?
            </a>
            <br />
            Don't have an account? <a href="/signup">Sign up</a>
          </p>
        )}

        {forgotPassword && (
          <p className="login-footer">
            <a
              href="#!"
              onClick={() => {
                setForgotPassword(false);
                setOtpSent(false);
              }}
            >
              Back to Login
            </a>
          </p>
        )}
      </div>
    </div>
  );
}
